import React, { useEffect, useRef, useState } from 'react';

import { HubIcon } from '@atlaskit/logo';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import { CustomLogo } from '@atlaskit/navigation-system/top-nav-items';

import { useCompanyHubName } from '@confluence/company-hub-utils/entry-points/useCompanyHubName.ts';
import { COMPANY_HUB } from '@confluence/named-routes/entry-points/routes.ts';

/**
 * The {@link https://staging.atlassian.design/components/navigation-system/top-navigation/examples#navigation-logo | navigation logo} of the Company Hub app-like experience at Team '25 US.
 *
 * Inspired by `SiteLogoButton` at confluence/next/packages/app-navigation/src/SiteLogoButton.tsx
 *
 * @see {@link https://www.figma.com/design/MBdUXhdmgbsIkMkOhQGu5e/Hub-App?node-id=101-52260}
 * @see {@link https://hello.atlassian.net/wiki/spaces/CLI/pages/4856024337/Confluence-less+Team+25+US+Experience}
 */
export const CompanyHubSiteLogoButton = (): React.JSX.Element => {
	const companyHubName = useCompanyHubName();

	return (
		<CustomLogo
			href={COMPANY_HUB.toUrl()}
			icon={CompanyHubNavIcon}
			label={companyHubName}
			logo={CompanyHubLogo}
		/>
	);
};

/**
 * The navigation logo at small viewport widths.
 */
const CompanyHubNavIcon = () => <HubIcon size="small" shouldUseNewLogoDesign />;

/**
 * The navigation logo at large viewport widths.
 */
function CompanyHubLogo() {
	const companyHubName = useCompanyHubName();
	const textRef = useRef<SVGTextElement>(null);
	const [textWidth, setTextWidth] = useState<number>(0);

	useEffect(() => {
		if (textRef.current) {
			const nameTextBox = textRef.current.getBBox();
			setTextWidth(nameTextBox.width);
		}
	}, [companyHubName]);

	return (
		<Flex gap="space.075" alignItems="center">
			<HubIcon size="small" shouldUseNewLogoDesign />
			<svg width={textWidth} height="32" viewBox={`0 0 ${textWidth} 32`}>
				<text
					ref={textRef}
					dominantBaseline="central"
					x="0"
					y="50%"
					fill={token('color.text')}
					fontFamily={token('font.family.heading')}
					fontSize={token('font.heading.xsmall')}
					fontWeight={token('font.weight.bold')}
				>
					{companyHubName}
				</text>
			</svg>
		</Flex>
	);
}
